import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Box, Typography, useMediaQuery, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, useScrollTrigger } from "@mui/material";
import logo from "../assets/logo/radix.png";
import whiteLogo from "../assets/logo/radix-reverse.png";
import { useNavigate, useLocation } from "react-router-dom";
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from "framer-motion";


function ElevationScroll(props) {
    const { children, window, setScrolled, isHome, activeSection } = props;
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 50,
        target: window ? window() : undefined,
    });

    // Background should be visible if:
    // 1. We are not on the home page
    // 2. OR we are on home but have scrolled out of the hero section (#about)
    // 3. OR the scroll trigger is fired (fallback)
    const showBg = !isHome || (activeSection !== "" && activeSection !== "#about") || trigger;

    useEffect(() => {
        setScrolled(showBg);
    }, [showBg, setScrolled]);

    return React.cloneElement(children, {
        style: {
            backgroundColor: showBg ? "#08244d" : "transparent",
            boxShadow: showBg ? "0 0 10px rgb(8 36 77 / 68%)" : "none",
            transition: "all 0.4s ease",
            paddingTop: showBg ? "0px" : "15px",
            paddingBottom: showBg ? "0px" : "15px",
            backdropFilter: showBg ? 'blur(15px)' : 'none',
        }
    });
}

const NavbarComponent = () => {
    const isSmallScreen = useMediaQuery('(max-width:985px)');
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState("");

    const isHome = location.pathname === "/" || location.pathname === "";
    // Show background if not on home, or if we've reached a section that isn't the hero section (#about)
    const showBg = !isHome || (activeSection !== "" && activeSection !== "#about");

    // Active Section Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(`#${entry.target.id}`);
                    }
                });
            },
            { threshold: 0.4 }
        );

        const sections = document.querySelectorAll("div[id]");
        sections.forEach((section) => observer.observe(section));

        return () => {
            sections.forEach((section) => observer.unobserve(section));
        };
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // Scroll to section handler
    const handleNavigation = (link) => {
        if (link === "#contact-us") {
            navigate("/contact");
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setMobileOpen(false);
            return;
        }

        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => {
                const section = document.querySelector(link);
                if (section) {
                    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        } else {
            const section = document.querySelector(link);
            if (section) {
                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        setMobileOpen(false);
    };

    const navItems = [
        { label: "ABOUT", link: "#about" },
        { label: "ADVANTAGE", link: "#competitive-advantage" },
        { label: "GOALS", link: "#strategic-goals" },
        { label: "VALUES", link: "#core-values" },
        { label: "MILITARY", link: "#military-defense" },
        { label: "CIVILIAN", link: "#civilian-defense" },
        { label: "PARTNERS", link: "#partners" },
        { label: "CONTACT", link: "#contact-us" },
    ];

    const drawer = (
        <Box sx={{ textAlign: 'center', height: '100%', pt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>
                <IconButton onClick={handleDrawerToggle}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ my: 2 }} onClick={() => navigate("/")}>
                <img src={logo} alt="Radix Logo" style={{ height: 40, cursor: 'pointer' }} />
            </Box>

            <List>
                {navItems.map((item) => (
                    <ListItem key={item.label} disablePadding>
                        <ListItemButton sx={{ textAlign: 'center' }} onClick={() => handleNavigation(item.link)}>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: 'bold',
                                        color: activeSection === item.link ? '#08244d' : '#555',
                                        borderBottom: activeSection === item.link ? '2px solid #08244d' : 'none',
                                        display: 'inline-block'
                                    }
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <React.Fragment>
            <ElevationScroll setScrolled={setScrolled} isHome={isHome} activeSection={activeSection}>
                <AppBar
                    position="fixed"
                    elevation={0}
                    sx={{
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        color: "white",
                        backgroundImage: 'none',
                    }}
                >
                    <Toolbar sx={{ justifyContent: 'space-between', px: isSmallScreen ? 2 : '5% !important' }}>

                        {/* Logo */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <motion.img
                                src={whiteLogo}
                                alt="Radix Logo"
                                style={{
                                    height: isSmallScreen ? 30 : 50,
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                onClick={() => navigate("/")}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            />
                        </Box>

                        {/* Desktop Navigation Links */}
                        {!isSmallScreen && (
                            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                {navItems.map((item) => (
                                    <Box key={item.label} sx={{ position: 'relative' }}>
                                        <Typography
                                            onClick={() => handleNavigation(item.link)}
                                            sx={{
                                                color: '#fff',
                                                fontWeight: 'bold',
                                                fontSize: '0.9rem',
                                                textDecoration: 'none',
                                                cursor: 'pointer',
                                                transition: 'color 0.3s',
                                                textShadow: showBg ? 'none' : '0 2px 8px rgba(0,0,0,0.6)',
                                                '&:hover': {
                                                    color: '#4facfe'
                                                }
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                        {activeSection === item.link && (
                                            <Box
                                                component={motion.div}
                                                layoutId="underline"
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: -5,
                                                    left: 0,
                                                    right: 0,
                                                    height: 2,
                                                    bgcolor: '#4facfe',
                                                    boxShadow: '0 0 8px #4facfe'
                                                }}
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Mobile Menu Icon */}
                        {isSmallScreen && (
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="end"
                                onClick={handleDrawerToggle}
                                sx={{ color: '#ffffff' }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                    </Toolbar>
                </AppBar>
            </ElevationScroll>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
                }}
            >
                {drawer}
            </Drawer>
        </React.Fragment>
    );
};

export default NavbarComponent;